import * as React from 'react'

import { DocumentsCustomFieldSettingsDocument } from 'generated/resources/graphql'
import { includes } from 'lodash'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useFeatures } from 'modules/core/hooks/useFeatures'

import CustomFields from 'modules/custom_resources/components/CustomFields'
import customFieldsByGroup from 'modules/custom_resources/store/collections/customFields/byGroup'
import ResourceFieldsCollection from 'modules/custom_resources/store/collections/customFields/resourceFields'
import CustomField from 'modules/custom_resources/store/models/customFields/Field'
import type { IResourceField } from 'modules/custom_resources/store/models/customFields/ResourceField'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { ALL_TYPES } from 'modules/documents/lib/constants'

interface Props {
  folder: Folder
}

const ToggleCustomFields = ({ folder }: Props) => {
  const { has_documents_custom_fields } = useFeatures(['documents_custom_fields'])
  const { loading: loadingPageQuery, data } = useQuery(DocumentsCustomFieldSettingsDocument, {})
  const { employee } = useCurrentSession()

  const canManageCustomFields =
    data?.customFields?._policies?.companyCreateCustomFields?.can ?? false

  const resourceFieldCollection = useMemo(
    () =>
      new ResourceFieldsCollection([], {
        model_ref: 'documents-folder',
        instance_id: folder.id,
      }),
    [folder.id]
  )
  const { loading: loadingCustomFields } = useUnsafeAsyncData(
    async () => {
      const usageGroups = [CustomField.UsageGroups.documents, CustomField.UsageGroups.expenses]

      const customFieldsPromise = customFieldsByGroup.fetch({
        data: {
          usage_group_slug: usageGroups,
        },
      })

      const resourceFieldsPromise = resourceFieldCollection.byUsageGroup(usageGroups, employee?.id)

      await Promise.all([customFieldsPromise, resourceFieldsPromise])
    },
    [resourceFieldCollection],
    {
      errorFlash: true,
    }
  )

  const loading = loadingPageQuery || loadingCustomFields

  const usageGroups = () => {
    const results = [CustomField.UsageGroups.documents]
    const groups = [ALL_TYPES.expenses_employee, ALL_TYPES.expenses_company]

    if (includes(groups, folder.type)) {
      results.push(CustomField.UsageGroups.expenses)
    }

    return results
  }

  const filteredCustomFields = () => {
    return customFieldsByGroup.models.filter((f) =>
      includes(usageGroups(), f.attributes.usage_group_slug)
    )
  }

  const createCustomField = (data: Record<string, string | number>) =>
    customFieldsByGroup.createForGroup(CustomField.UsageGroups.documents, data)

  if (loading) return <Loading />

  return (
    <CustomFields
      allowPermissions
      canEditCustomField={canManageCustomFields && has_documents_custom_fields}
      canCreateCustomField={canManageCustomFields && has_documents_custom_fields}
      canDeleteCustomField={canManageCustomFields && has_documents_custom_fields}
      createCustomField={createCustomField}
      createResourceField={(data: Partial<IResourceField>) => resourceFieldCollection.create(data)}
      customFields={filteredCustomFields()}
      resourceFields={resourceFieldCollection.models}
      hideCustomResourceAssignment={!canManageCustomFields}
    />
  )
}

export default observer(ToggleCustomFields)
